import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Collections", path: "/collections" },
  { label: "Gallery", path: "/gallery" },
  { label: "About", path: "/about" },
];

const WHATSAPP_NUMBER = "923335281400";
const WHATSAPP_MESSAGE = "Hello! I'm interested in your furniture collection.";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-brand-cream shadow-sm py-2" : "bg-gradient-to-b from-[#1a0f0a]/90 via-[#1a0f0a]/50 to-transparent md:from-[#1a0f0a]/75 md:via-[#1a0f0a]/30 py-3 md:py-4"
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between gap-6">

        {/* ── LOGO + BRAND NAME ─────────────────────────────────── */}
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img
            src="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/937748a816123b3aa330f9d4b73acce1.png"
            alt="M. Hayat & Co. Logo"
            className="h-10 md:h-14 w-auto object-contain"
          />
          <div className="flex flex-col justify-center leading-none">
            <span
              className={`font-serif font-bold text-sm md:text-base tracking-wide transition-colors duration-300 ${
                scrolled ? "text-brand-darkbrown" : "text-white"
              }`}
            >
              M. Hayat &amp; Co.
            </span>
            <span
              className={`font-display text-[9px] tracking-[3px] uppercase mt-0.5 transition-colors duration-300 ${
                scrolled ? "text-brand-gold" : "text-brand-gold/80"
              }`}
            >
              Est. 1870
            </span>
          </div>
        </Link>

        {/* ── DESKTOP NAV ───────────────────────────────────────── */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-display text-xs font-medium tracking-[3px] uppercase transition-colors duration-300 relative group ${
                scrolled
                  ? location.pathname === link.path
                    ? "text-brand-gold"
                    : "text-brand-darkbrown hover:text-brand-gold"
                  : location.pathname === link.path
                  ? "text-brand-gold"
                  : "text-white/90 hover:text-brand-goldlight"
              }`}
            >
              {link.label}
              <span
                className={`absolute -bottom-1 left-0 h-px bg-brand-gold transition-all duration-300 ${
                  location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </Link>
          ))}
        </nav>

        {/* ── WHATSAPP CTA ──────────────────────────────────────── */}
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`hidden md:inline-flex whitespace-nowrap items-center gap-2 font-display text-[11px] font-semibold tracking-[2px] uppercase px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer ${
            scrolled
              ? "bg-brand-gold text-brand-darkbrown hover:bg-brand-midbrown hover:text-brand-goldlight border border-brand-gold"
              : "bg-transparent text-brand-gold border border-brand-gold hover:bg-brand-gold hover:text-brand-darkbrown"
          }`}
        >
          <i className="ri-whatsapp-line text-base" />
          WhatsApp Us
        </a>

        {/* ── MOBILE TOGGLE ─────────────────────────────────────── */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
            scrolled ? "text-brand-darkbrown" : "text-white"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
        </button>
      </div>

      {/* ── MOBILE MENU ───────────────────────────────────────── */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-400 bg-brand-cream ${
          mobileOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-4 border-t border-brand-paper">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-display text-sm tracking-widest uppercase transition-colors duration-200 ${
                location.pathname === link.path
                  ? "text-brand-gold"
                  : "text-brand-darkbrown hover:text-brand-gold"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center justify-center gap-2 font-display text-xs tracking-widest uppercase px-6 py-2.5 bg-brand-gold text-brand-darkbrown rounded-full mt-2 cursor-pointer hover:bg-brand-midbrown hover:text-brand-goldlight transition-all duration-300"
          >
            <i className="ri-whatsapp-line text-base" />
            WhatsApp Us
          </a>
        </div>
      </div>
    </header>
  );
}
